import { useState } from "react"
import { useAlgoliaClient } from "~/helpers/algolia"
import { capsulePagePath, collectionPath, productPath } from "~/helpers/routes"
import { useConfigContext } from "~/providers/ConfigProvider"

interface State {
  suggestions: Suggestion[]
  activeSuggestionIndex: number | null
}

interface Suggestion {
  id: string
  text: string
  href?: string
  badge?: string
}

interface UseSearch {
  fetchSuggestions: (query: string) => Promise<void>
  suggestions: Suggestion[]
  activeSuggestionIndex: number | null
  clearSuggestions: () => void
  clearActiveSuggestionIndex: () => void
  nextSuggestion: () => void
  previousSugggestion: () => void
}

const useSearch = (): UseSearch => {
  const searchClient = useAlgoliaClient()
  const { algoliaIndexes, disableEcomm } = useConfigContext()
  const [{ suggestions, activeSuggestionIndex }, setState] = useState<State>({
    suggestions: [],
    activeSuggestionIndex: null
  })

  const fetchSuggestions = async (query: string) => {
    if (query.length < 3) return

    const queries: any[] = [
      {
        indexName: "Suggestions",
        query,
        params: {
          responseFields: ["hits"],
          attributesToRetrieve: [],
          hitsPerPage: 6
        }
      }
    ]

    const productsResultIndex = queries.length
    queries.push({
      indexName: algoliaIndexes.products,
      query,
      params: {
        responseFields: ["hits"],
        restrictSearchableAttributes: ["title"],
        attributesToRetrieve: ["title", "handle"],
        hitsPerPage: 3
      }
    })

    const capsulePagesResultIndex = queries.length
    queries.push({
      indexName: algoliaIndexes.capsulePages,
      query,
      params: {
        responseFields: ["hits"],
        restrictSearchableAttributes: ["name"],
        attributesToRetrieve: ["name", "slug"],
        hitsPerPage: 3
      }
    })

    const collectionsResultIndex = disableEcomm ? null : queries.length
    if (!disableEcomm) {
      queries.push({
        indexName: algoliaIndexes.collections,
        query,
        params: {
          responseFields: ["hits"],
          restrictSearchableAttributes: ["title"],
          attributesToRetrieve: ["title", "handle"],
          hitsPerPage: 3
        }
      })
    }

    const response = (await searchClient.search(queries)) as any

    const completions: Suggestion[] = response.results[0].hits
      .map((hit: { objectID: any }) => hit.objectID)
      .filter((text: string) => text != query)
      .map((text: string) => ({
        id: text,
        text
      }))

    const products: Suggestion[] = response.results[
      productsResultIndex
    ].hits.map((hit: any) => ({
      id: hit.objectID,
      text: hit.title,
      href: productPath(hit.handle),
      badge: "Shop Product"
    }))

    const capsulePages: Suggestion[] = response.results[
      capsulePagesResultIndex
    ].hits.map((hit: any) => ({
      id: hit.objectID,
      text: hit.name,
      href: hit.slug ? capsulePagePath(hit.slug) : undefined
    }))

    const collections: Suggestion[] =
      collectionsResultIndex === null
        ? []
        : response.results[collectionsResultIndex].hits.map((hit: any) => ({
            id: hit.objectID,
            text: hit.title,
            href: collectionPath(hit.handle),
            badge: "Shop Collection"
          }))

    setState({
      suggestions: [
        ...completions,
        ...products,
        ...capsulePages,
        ...collections
      ],
      activeSuggestionIndex: null
    })
  }

  const clearSuggestions = () =>
    setState({ suggestions: [], activeSuggestionIndex: null })

  const clearActiveSuggestionIndex = () =>
    setState(s => ({ ...s, activeSuggestionIndex: null }))

  const nextSuggestion = () =>
    setState(s =>
      s.activeSuggestionIndex === null
        ? { ...s, activeSuggestionIndex: 0 }
        : s.activeSuggestionIndex < s.suggestions.length - 1
          ? { ...s, activeSuggestionIndex: s.activeSuggestionIndex + 1 }
          : { ...s, activeSuggestionIndex: null }
    )

  const previousSugggestion = () =>
    setState(s =>
      s.activeSuggestionIndex === null
        ? { ...s, activeSuggestionIndex: s.suggestions.length - 1 }
        : s.activeSuggestionIndex > 0
          ? { ...s, activeSuggestionIndex: s.activeSuggestionIndex - 1 }
          : { ...s, activeSuggestionIndex: null }
    )

  return {
    fetchSuggestions,
    suggestions,
    activeSuggestionIndex,
    clearSuggestions,
    clearActiveSuggestionIndex,
    nextSuggestion,
    previousSugggestion
  }
}

export default useSearch
